import { Portal, Root, Trigger } from '@radix-ui/react-dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { Checkbox } from '@/components/commons/checkbox'
import { Chip } from '@/components/commons/chip'
import { ErrorBox } from '@/components/commons/error-box'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DropdownContent, DropdownItem } from '@/components/ui/dropdown-atoms'
import {
	AlertTriangleIcon,
	DotsHorizontalIcon,
	Trash01Icon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useDeleteExperienceShellPhotos } from '@/features/experience-shell/queries/use-delete-experience-shell-photos.ts'
import { useEditExperienceShellPhotoCover } from '@/features/experience-shell/queries/use-edit-experience-shell-photo-cover.ts'
import { CircularProgressBar } from '@/features/service-vetting/components/circular-progress-bar'
import {
	type GalleryItem,
	useExperienceShellPhotoUpload
} from '@/features/service-vetting/store/services-photo-upload.ts'
import { QUERY_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'
export const PhotosStepGalleryItem = ({
	image,
	galleryId,
	experienceId
}: {
	image: GalleryItem
	galleryId: number
	experienceId: number
}) => {
	const uploadProgressValues = useExperienceShellPhotoUpload(
		(store) => store.uploadProgressValues
	)
	const deleteGalleryItem = useExperienceShellPhotoUpload(
		(store) => store.deleteGalleryItem
	)
	const setGalleryItemIsCover = useExperienceShellPhotoUpload(
		(store) => store.setGalleryItemIsCover
	)
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const deletePhoto = useDeleteExperienceShellPhotos(
		{ galleryId, experienceId },
		{
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [
						QUERY_KEYS.EXPERIENCE_SHELL_GALLERY_IMAGES,
						experienceId
					]
				})
				deleteGalleryItem(image.id)
			}
		}
	)

	const editPhotoCover = useEditExperienceShellPhotoCover(
		{ galleryId, experienceId, imagesId: image.id },
		{
			onSuccess: () => {
				setGalleryItemIsCover({ id: image.id, isCover: !image.isCover })
			}
		}
	)

	const onClickDeletePhoto = () => {
		image.status === 'uploaded'
			? deletePhoto.mutate({ imageIds: [image.id] })
			: deleteGalleryItem(image.id)
	}

	const onClickSetAsCover = () => {
		editPhotoCover.mutate({ isCover: !image.isCover })
	}

	return (
		<GalleryImageItem
			centerSlot={
				image.status === 'pending' ? (
					<CircularProgressBar
						progress={uploadProgressValues[image.id] || 0}
					/>
				) : (
					image.status === 'error' && (
						<ErrorBox
							icon={<AlertTriangleIcon />}
							title={t('errors:default')}
						/>
					)
				)
			}
			hasBlur={image.status !== 'uploaded'}
			src={image.thumbnails?.xs || image.url}
			topLeftSlot={
				image.status === 'uploaded' &&
				image.isCover && (
					<Chip
						label={t('experience_shell:cover')}
						shape={'circle'}
						variant={'neutral'}
					/>
				)
			}
			topRightSlot={
				image.status === 'uploaded' && (
					<Root>
						<Trigger asChild>
							<ButtonLegacy
								shape='square'
								size='xs'
								type='button'
								variant='secondary'
							>
								<DotsHorizontalIcon />
							</ButtonLegacy>
						</Trigger>
						<Portal>
							<DropdownContent
								align='end'
								side='bottom'
								sideOffset={4}
							>
								<DropdownItem
									disabled={editPhotoCover.isPending}
									onClick={onClickSetAsCover}
								>
									<Checkbox
										defaultChecked={image.isCover}
										disabled={editPhotoCover.isPending}
										label={t(
											'experience_shell:set_as_cover'
										)}
									/>
								</DropdownItem>
								<DropdownItem
									disabled={deletePhoto.isPending}
									onClick={(event) => {
										event.preventDefault()
										onClickDeletePhoto()
									}}
								>
									<div className='flex items-center gap-2'>
										{deletePhoto.isPending ? (
											<Spinner size={20} />
										) : (
											<Trash01Icon size={20} />
										)}
										{t('common:delete')}
									</div>
								</DropdownItem>
							</DropdownContent>
						</Portal>
					</Root>
				)
			}
		/>
	)
}
